// scheduling/backend-actions-client.ts
import type {
    BackendActionsClient,
    HasSendAttemptResponse,
    AcquireSlotResponse,
    DeleteAndLogResponse,
    CheckEngineCmdLockResponse,
    ClientClock,
} from './send-protocol.types'

// Must list EVERY reason acquire-send-slot can return. A missing entry is not a no-op: the reason is
// silently rewritten to `granted`/`backoff`, and the orchestrator then takes the wrong branch for it.
// `gateway_preferred` was missing (#172) — so the BE#167 expected-wait early-return never fired, and
// every grace-window deny ran a store-check and wrote a row blaming a contested lock.
const SLOT_REASONS: ReadonlyArray<AcquireSlotResponse['reason']> = [
    'granted', 'backoff', 'exhausted', 'window_expired', 'not_found', 'already_terminal',
    'gateway_preferred', 'not_yet_due',
]
function coerceSlotReason(v: unknown, allowToSend: boolean): AcquireSlotResponse['reason'] {
    return SLOT_REASONS.includes(v as AcquireSlotResponse['reason'])
        ? (v as AcquireSlotResponse['reason'])
        : (allowToSend ? 'granted' : 'backoff')
}

/** Caller-injected HTTP POST. Cross-runtime — pod wires axios; extension
 *  wires its `apiCall` helper. The orchestrator never sees this — it talks
 *  to the BackendActionsClient interface only. */
export type BackendActionsPost = (path: string, body: unknown) => Promise<any>

/** The browser/Node system clock as a ClientClock reading. */
export function systemClientClock(): ClientClock {
    const nowMs = Date.now()
    // getTimezoneOffset() is minutes WEST of UTC (Sydney in summer = -660); we send minutes EAST.
    return { nowMs, tzOffsetMin: -new Date(nowMs).getTimezoneOffset() + 0 }
}

export interface CreateBackendActionsClientOptions {
    post: BackendActionsPost
    /** FE#447: when wired, EVERY slot / can-i-send request carries the client's own clock reading
     *  (`clientNowMs`, `clientTzOffsetMin`), sampled at the moment of the request. Opt-in so a pod (whose
     *  clock is the server's) and existing callers send exactly what they always did. */
    clock?: () => ClientClock
    log?: (level: 'warn' | 'debug', msg: string, extra?: unknown) => void
}

export function createBackendActionsClient(opts: CreateBackendActionsClientOptions): BackendActionsClient {
    const { post, log, clock } = opts
    /** One fresh reading, as body fields — or nothing at all: a missing/broken/non-finite clock must
     *  never cost a send, the server simply falls back to its own time. */
    const clockFields = (): { clientNowMs?: number; clientTzOffsetMin?: number } => {
        if (!clock) return {}
        try {
            const c = clock()
            if (!Number.isFinite(c.nowMs)) return {}
            return Number.isFinite(c.tzOffsetMin)
                ? { clientNowMs: c.nowMs, clientTzOffsetMin: c.tzOffsetMin }
                : { clientNowMs: c.nowMs }
        } catch {
            return {}
        }
    }
    return {
        async hasSendAttemptBeenMadeToMessage(args): Promise<HasSendAttemptResponse | undefined> {
            try {
                const resp = await post('/actions?cmd=has-send-attempt-been-made-to-message', {
                    messageId: args.messageId,
                    retryAttemptNumber: args.retryAttemptNumber,
                    isSendMessageNow: args.isSendMessageNow,
                    ...clockFields(),
                })
                if (!resp) return undefined
                return {
                    allowToSend: !!resp.allowToSend,
                    initialAttemptLockExists: !!resp.initialAttemptLockExists,
                    currentAttemptCount: typeof resp.currentAttemptCount === 'number' ? resp.currentAttemptCount : 0,
                    wasSendAttempted: !!resp.wasSendAttempted,
                    userMessage: resp.userMessage,
                    error: resp.error,
                    // #451: forward only a literal true; an absent/other value stays absent so an older server is unchanged.
                    ...(resp.attemptCountAuthoritative === true ? { attemptCountAuthoritative: true } : {}),
                }
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : 'unknown'
                log?.('warn', `[backend-actions] hasSendAttemptBeenMadeToMessage failed: ${msg}`)
                return undefined
            }
        },

        async acquireSendSlot(args): Promise<AcquireSlotResponse | undefined> {
            try {
                const resp = await post('/actions?cmd=acquire-send-slot', {
                    messageId: args.messageId,
                    isSendMessageNow: args.isSendMessageNow,
                    clientType: args.clientType,
                    extensionVersion: args.extensionVersion,
                    // Per-message send metadata — forwarded alongside extensionVersion.
                    isGroup: args.isGroup,
                    isCommunity: args.isCommunity,
                    isParentGroup: args.isParentGroup,
                    announce: args.announce,
                    groupType: args.groupType,
                    parentGroupId: args.parentGroupId,
                    messageType: args.messageType,
                    sendErrorReason: args.sendErrorReason,
                    recipientAddressing: args.recipientAddressing,
                    ...clockFields(),
                })
                if (!resp) return undefined
                const allowToSend = !!resp.allowToSend
                return {
                    allowToSend,
                    attemptNumber: typeof resp.attemptNumber === 'number' ? resp.attemptNumber : 0,
                    reason: coerceSlotReason(resp.reason, allowToSend),
                    // Carry the full userMessage through (engine reads send content off it) — mirrors
                    // hasSendAttemptBeenMadeToMessage. resp is the untyped HTTP body; the runtime object
                    // includes message/asset/poll even though SendProtocolUserMessage doesn't name them.
                    userMessage: resp.userMessage,
                    ...(typeof resp.serverNowMs === 'number' && Number.isFinite(resp.serverNowMs)
                        ? { serverNowMs: resp.serverNowMs }
                        : {}),
                    // FE#447: on 'not_yet_due' the server says (on ITS timeline) when to come back.
                    ...(typeof resp.retryAfterMs === 'number' && Number.isFinite(resp.retryAfterMs) && resp.retryAfterMs > 0
                        ? { retryAfterMs: resp.retryAfterMs }
                        : {}),
                }
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : 'unknown'
                log?.('warn', `[backend-actions] acquireSendSlot failed: ${msg}`)
                return undefined
            }
        },

        async createMessageAttemptLog(args): Promise<{ success: boolean; error?: string }> {
            try {
                const resp = await post('/actions?cmd=create-message-attempt-log', { ...args })
                return { success: !!resp?.success, error: resp?.error }
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : 'unknown'
                log?.('warn', `[backend-actions] createMessageAttemptLog failed: ${msg}`)
                return { success: false, error: msg }
            }
        },

        async deleteUserMessageAndCreateLog(args): Promise<DeleteAndLogResponse> {
            // Intentionally NOT swallowed — caller (orchestrator) must observe
            // failure to avoid silently dropping sentCount writes.
            const resp = await post('/actions?cmd=delete-user-message-and-create-log', { ...args })
            return {
                isLogCreated: !!resp?.isLogCreated,
                isMessageDeleted: !!resp?.isMessageDeleted,
                isRecurringScheduled: !!resp?.isRecurringScheduled,
                newMessage: resp?.newMessage,
                message: resp?.message,
            }
        },

        async checkEngineCmdLock(args): Promise<CheckEngineCmdLockResponse | undefined> {
            try {
                const body: Record<string, unknown> = { engineCmdId: args.engineCmdId }
                if (typeof args.ttlSec === 'number') body.ttlSec = args.ttlSec
                const resp = await post('/actions?cmd=check-engine-cmd-lock', body)
                if (!resp) return undefined
                return {
                    acquired: !!resp.acquired,
                    lockId: typeof resp.lockId === 'string' ? resp.lockId : args.engineCmdId,
                    acquired_at: typeof resp.acquired_at === 'string' ? resp.acquired_at : undefined,
                }
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : 'unknown'
                log?.('warn', `[backend-actions] checkEngineCmdLock failed: ${msg}`)
                return undefined
            }
        },
    }
}
